import React, { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import VoiceLiveQuestionnaire from "./VoiceLiveQuestionnaire";

const STEP_LABELS = [
  "Basic Info",
  "Symptoms",
  "Medication & Current Illness",
  "History & Tests",
];

const STEP_HELPERS = [
  "Patient identity",
  "Current complaints",
  "Medicine and active illness",
  "Past and family context",
];

const DEFAULT_DATA = {
  is_self: false,
  patient_name: "",
  age: "",
  gender: "",
  relation: "",
  symptoms: "",
  duration: "",
  severity: "",
  diet: "",
  current_medications: "",
  currently_suffering_from: "",
  earlier_disease: "",
  past_surgery: "",
  past_test_history: "",
  family_history_major_illnesses: "",
};

function IntakeSignal({ step, inputMode, saving }) {
  const bars = [
    {
      label: "Profile",
      value: step >= 0 ? 80 : 20,
      color: "linear-gradient(180deg, #18b7ff, #1f7bc1)",
    },
    {
      label: "Symptoms",
      value: step >= 1 ? 76 : 25,
      color: "linear-gradient(180deg, #62d7ff, #2d8fe0)",
    },
    {
      label: "Medicine",
      value: step >= 2 ? 68 : 22,
      color: "linear-gradient(180deg, #94d8ff, #1769a9)",
    },
    {
      label: "History",
      value: step >= 3 ? 92 : 24,
      color: "linear-gradient(180deg, #fbbf24, #f59e0b)",
    },
  ];

  return (
    <aside className="mgq-signal-card">
      <div className="mgq-signal-top">
        <div>
          <span>Intake Signal</span>
          <strong>{saving ? "Saving intake" : "Workspace ready"}</strong>
        </div>
        <em>{inputMode === "voice" ? "Voice" : "Text"}</em>
      </div>

      <p>
        Capture patient context in structured steps, then generate a connected
        MedGen clinical analysis.
      </p>

      <div className="mgq-bars">
        {bars.map((bar) => (
          <div className="mgq-bar-item" key={bar.label}>
            <div className="mgq-bar-track">
              <i style={{ height: `${bar.value}%`, background: bar.color }} />
            </div>
            <small>{bar.label}</small>
          </div>
        ))}
      </div>
    </aside>
  );
}

function FieldBlock({
  label,
  children,
  full = false,
  hint = "",
}) {
  return (
    <div className={full ? "mgq-field full-span" : "mgq-field"}>
      <label>{label}</label>
      {children}
      {hint ? <small>{hint}</small> : null}
    </div>
  );
}

export default function QuestionnaireStepper({
  sessionId,
  patient,
  initialData,
  onSaved,
}) {
  const mergedInitial = useMemo(() => {
    return {
      ...DEFAULT_DATA,
      patient_name: patient?.name || "",
      age: patient?.age || "",
      gender: patient?.gender || "",
      relation: patient?.relation || "self",
      is_self: (patient?.relation || "self") === "self",
      ...(initialData || {}),
    };
  }, [patient, initialData]);

  const [inputMode, setInputMode] = useState("text");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(mergedInitial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(mergedInitial);
  }, [mergedInitial]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const next = () => setStep((prev) => Math.min(prev + 1, STEP_LABELS.length - 1));
  const prev = () => setStep((prev) => Math.max(prev - 1, 0));

  const validateStep = () => {
    if (step === 0) return form.patient_name?.trim()?.length > 1;
    if (step === 1) return form.symptoms?.trim()?.length > 2;
    return true;
  };

  const buildPayloadData = (sourceForm) => ({
    ...sourceForm,
    age:
      sourceForm.age === "" || sourceForm.age === null || sourceForm.age === undefined
        ? null
        : Number(sourceForm.age),
    is_self: (sourceForm.relation || "self") === "self",
  });

  const saveQuestionnaire = async (overrideForm = null) => {
    try {
      setSaving(true);

      const finalForm = overrideForm || form;

      const payload = {
        session_id: sessionId,
        data: buildPayloadData(finalForm),
      };

      const res = await api.post("/api/questionnaire/save", payload);
      setForm(payload.data);
      onSaved?.(payload.data, res.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not save questionnaire");
    } finally {
      setSaving(false);
    }
  };

  const handleVoiceCompleted = async (voiceData) => {
    const mergedForm = {
      ...form,
      ...voiceData,
    };

    setForm(mergedForm);
    await saveQuestionnaire(mergedForm);
  };

  const progressPercent = Math.round(((step + 1) / STEP_LABELS.length) * 100);

  return (
    <div className="mgq-shell">
      <style>{questionnaireCss}</style>

      <section className="mgq-hero">
        <div className="mgq-hero-copy">
          <div className="mgq-kicker">Patient Intake</div>
          <h2>Questionnaire</h2>
          <p>
            Choose text or live voice mode for structured patient intake. The
            saved intake will trigger MedGen analysis for this clinical session.
          </p>

          <div className="mgq-mode-switch">
            <button
              type="button"
              className={inputMode === "text" ? "mgq-mode active" : "mgq-mode"}
              onClick={() => setInputMode("text")}
              disabled={saving}
            >
              <b>Text Mode</b>
              <span>Manual structured entry</span>
            </button>

            <button
              type="button"
              className={inputMode === "voice" ? "mgq-mode active" : "mgq-mode"}
              onClick={() => setInputMode("voice")}
              disabled={saving}
            >
              <b>Voice Mode</b>
              <span>Live guided intake</span>
            </button>
          </div>
        </div>

        <IntakeSignal step={step} inputMode={inputMode} saving={saving} />
      </section>

      {inputMode === "voice" ? (
        <div className="mgq-voice-wrap">
          <VoiceLiveQuestionnaire
            sessionId={sessionId}
            initialData={form}
            onComplete={handleVoiceCompleted}
          />
        </div>
      ) : (
        <section className="mgq-form-card">
          <div className="mgq-progress-head">
            <div>
              <span>Step {step + 1} of {STEP_LABELS.length}</span>
              <strong>{STEP_LABELS[step]}</strong>
              <p>{STEP_HELPERS[step]}</p>
            </div>

            <div className="mgq-progress-ring">
              <div
                className="mgq-ring"
                style={{ "--mgq-progress": `${progressPercent}%` }}
              >
                <b>{progressPercent}%</b>
              </div>
            </div>
          </div>

          <div className="mgq-step-strip">
            {STEP_LABELS.map((label, index) => (
              <button
                key={label}
                type="button"
                className={`mgq-step ${index === step ? "active" : ""} ${
                  index < step ? "done" : ""
                }`}
                onClick={() => setStep(index)}
              >
                <span>{index + 1}</span>
                <div>
                  <b>{label}</b>
                  <small>{STEP_HELPERS[index]}</small>
                </div>
              </button>
            ))}
          </div>

          <div className="mgq-form-body">
            {step === 0 && (
              <div className="mgq-grid">
                <FieldBlock label="Patient Name">
                  <input
                    value={form.patient_name}
                    onChange={(e) => updateField("patient_name", e.target.value)}
                    placeholder="Patient name"
                  />
                </FieldBlock>

                <FieldBlock label="Age">
                  <input
                    type="number"
                    value={form.age}
                    onChange={(e) => updateField("age", e.target.value)}
                    placeholder="Age"
                  />
                </FieldBlock>

                <FieldBlock label="Gender">
                  <select
                    value={form.gender}
                    onChange={(e) => updateField("gender", e.target.value)}
                  >
                    <option value="">Select gender</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="other">Other</option>
                  </select>
                </FieldBlock>

                <FieldBlock label="Relation">
                  <select
                    value={form.relation}
                    onChange={(e) => updateField("relation", e.target.value)}
                  >
                    <option value="self">Self</option>
                    <option value="mother">Mother</option>
                    <option value="father">Father</option>
                    <option value="spouse">Spouse</option>
                    <option value="child">Child</option>
                    <option value="sibling">Sibling</option>
                    <option value="other">Other</option>
                  </select>
                </FieldBlock>
              </div>
            )}

            {step === 1 && (
              <div className="mgq-grid">
                <FieldBlock
                  label="Symptoms"
                  full
                  hint="Describe the main complaints, pattern, frequency, triggers, and current discomfort."
                >
                  <textarea
                    value={form.symptoms}
                    onChange={(e) => updateField("symptoms", e.target.value)}
                    placeholder="Describe symptoms clearly"
                  />
                </FieldBlock>

                <FieldBlock label="Duration">
                  <input
                    value={form.duration}
                    onChange={(e) => updateField("duration", e.target.value)}
                    placeholder="e.g. 3 days, 2 weeks, 3 years"
                  />
                </FieldBlock>

                <FieldBlock label="Severity">
                  <select
                    value={form.severity}
                    onChange={(e) => updateField("severity", e.target.value)}
                  >
                    <option value="">Select severity</option>
                    <option value="mild">Mild</option>
                    <option value="moderate">Moderate</option>
                    <option value="severe">Severe</option>
                  </select>
                </FieldBlock>
              </div>
            )}

            {step === 2 && (
              <div className="mgq-grid">
                <FieldBlock label="Diet" full>
                  <textarea
                    value={form.diet}
                    onChange={(e) => updateField("diet", e.target.value)}
                    placeholder="Dietary pattern, appetite, hydration, restrictions"
                  />
                </FieldBlock>

                <FieldBlock label="Current Medications" full>
                  <textarea
                    value={form.current_medications}
                    onChange={(e) =>
                      updateField("current_medications", e.target.value)
                    }
                    placeholder="Mention all current medicines, supplements, or regular drugs"
                  />
                </FieldBlock>

                <FieldBlock label="Currently Suffering From" full>
                  <textarea
                    value={form.currently_suffering_from}
                    onChange={(e) =>
                      updateField("currently_suffering_from", e.target.value)
                    }
                    placeholder="Current major conditions, infections, chronic issues, or ongoing diagnosis"
                  />
                </FieldBlock>
              </div>
            )}

            {step === 3 && (
              <div className="mgq-grid">
                <FieldBlock label="Earlier Disease" full>
                  <textarea
                    value={form.earlier_disease}
                    onChange={(e) => updateField("earlier_disease", e.target.value)}
                    placeholder="Past illnesses, recurring problems, known medical history"
                  />
                </FieldBlock>

                <FieldBlock label="Past Surgery" full>
                  <textarea
                    value={form.past_surgery}
                    onChange={(e) => updateField("past_surgery", e.target.value)}
                    placeholder="Surgery history, procedure names, approximate dates"
                  />
                </FieldBlock>

                <FieldBlock label="Past Test History" full>
                  <textarea
                    value={form.past_test_history}
                    onChange={(e) =>
                      updateField("past_test_history", e.target.value)
                    }
                    placeholder="Previous tests, scans, blood reports, MRI/CT/X-ray, notable findings"
                  />
                </FieldBlock>

                <FieldBlock label="Family History Major Illnesses" full>
                  <textarea
                    value={form.family_history_major_illnesses}
                    onChange={(e) =>
                      updateField("family_history_major_illnesses", e.target.value)
                    }
                    placeholder="Family disease history, genetic risks, diabetes, heart disease, neurological illness"
                  />
                </FieldBlock>
              </div>
            )}
          </div>

          <div className="mgq-footer">
            <button
              className="mgq-btn ghost"
              type="button"
              onClick={prev}
              disabled={step === 0 || saving}
            >
              Back
            </button>

            {step < STEP_LABELS.length - 1 ? (
              <button
                className="mgq-btn primary"
                type="button"
                onClick={next}
                disabled={!validateStep() || saving}
              >
                Next Step
              </button>
            ) : (
              <button
                className="mgq-btn primary"
                type="button"
                onClick={() => saveQuestionnaire()}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save & Generate Analysis"}
              </button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

const questionnaireCss = `
.mgq-shell {
  --mg-black: #03060c;
  --mg-navy: #06101f;
  --mg-navy-2: #081525;
  --mg-blue: #1f7bc1;
  --mg-cyan: #18b7ff;
  --mg-text: #07111f;
  --mg-muted: #66778e;
  --mg-border: #cddcec;
  --mg-soft: #f6faff;

  width: 100%;
  color: var(--mg-text);
}

.mgq-shell * {
  box-sizing: border-box;
}

.mgq-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 390px;
  gap: 22px;
  align-items: stretch;
  margin-bottom: 18px;
}

.mgq-hero-copy {
  padding: 26px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid var(--mg-border);
  box-shadow: 0 14px 38px rgba(7, 17, 31, 0.06);
}

.mgq-kicker {
  margin-bottom: 12px;
  color: #125a94;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.mgq-hero h2 {
  margin: 0;
  color: var(--mg-text);
  font-size: clamp(32px, 3vw, 46px);
  line-height: 1.03;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.mgq-hero p {
  max-width: 760px;
  margin: 16px 0 18px;
  color: var(--mg-muted);
  font-size: 15px;
  line-height: 1.68;
}

.mgq-mode-switch {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.mgq-mode {
  min-height: 78px;
  padding: 15px;
  border-radius: 16px;
  border: 1px solid var(--mg-border);
  background: #f8fbff;
  cursor: pointer;
  text-align: left;
  display: grid;
  gap: 5px;
  transition: 0.16s ease;
}

.mgq-mode b {
  color: var(--mg-text);
  font-size: 14px;
  font-weight: 950;
}

.mgq-mode span {
  color: var(--mg-muted);
  font-size: 12px;
  font-weight: 750;
}

.mgq-mode.active {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  border-color: transparent;
  box-shadow: 0 14px 28px rgba(31, 123, 193, 0.20);
}

.mgq-mode.active b,
.mgq-mode.active span {
  color: white;
}

.mgq-mode:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.mgq-signal-card {
  padding: 22px;
  border-radius: 22px;
  color: white;
  background:
    radial-gradient(circle at 92% 0%, rgba(24, 183, 255, 0.20), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border: 1px solid rgba(24,183,255,0.18);
  box-shadow: 0 20px 55px rgba(3, 6, 12, 0.16);
}

.mgq-signal-top {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.mgq-signal-top span {
  display: block;
  margin-bottom: 8px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.mgq-signal-top strong {
  color: white;
  font-size: 22px;
  letter-spacing: -0.04em;
}

.mgq-signal-top em {
  padding: 5px 10px;
  border-radius: 999px;
  font-style: normal;
  color: #bfeaff;
  background: rgba(24,183,255,0.12);
  border: 1px solid rgba(24,183,255,0.18);
  font-size: 11px;
  font-weight: 900;
}

.mgq-signal-card p {
  color: #a9bdd3;
  margin: 18px 0;
  line-height: 1.65;
}

.mgq-bars {
  height: 145px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  align-items: end;
}

.mgq-bar-item {
  display: grid;
  justify-items: center;
  gap: 7px;
}

.mgq-bar-track {
  width: 48px;
  height: 90px;
  display: flex;
  align-items: end;
  padding: 5px;
  border-radius: 16px;
  background: rgba(255,255,255,0.10);
  border: 1px solid rgba(255,255,255,0.10);
}

.mgq-bar-track i {
  width: 100%;
  min-height: 18px;
  border-radius: 12px;
}

.mgq-bar-item small {
  color: #a9bdd3;
  font-size: 11px;
  font-weight: 850;
}

.mgq-voice-wrap,
.mgq-form-card {
  border-radius: 24px;
  background: rgba(255,255,255,0.96);
  border: 1px solid var(--mg-border);
  box-shadow: 0 14px 38px rgba(7, 17, 31, 0.06);
}

.mgq-voice-wrap {
  padding: 20px;
}

.mgq-form-card {
  overflow: hidden;
}

.mgq-progress-head {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: center;
  padding: 24px 26px;
  background:
    radial-gradient(circle at 96% 0%, rgba(31, 123, 193, 0.10), transparent 32%),
    linear-gradient(180deg, #ffffff, #f7fbff);
  border-bottom: 1px solid var(--mg-border);
}

.mgq-progress-head span {
  display: block;
  margin-bottom: 7px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.mgq-progress-head strong {
  color: var(--mg-text);
  font-size: 24px;
  font-weight: 950;
  letter-spacing: -0.045em;
}

.mgq-progress-head p {
  margin: 7px 0 0;
  color: var(--mg-muted);
  line-height: 1.5;
}

.mgq-progress-ring {
  flex: 0 0 auto;
}

.mgq-ring {
  width: 78px;
  height: 78px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background:
    radial-gradient(circle at center, #ffffff 0 56%, transparent 57%),
    conic-gradient(#1f7bc1 var(--mgq-progress), #e8eef6 0);
  box-shadow: inset 0 0 0 1px #dce7f2;
}

.mgq-ring b {
  color: var(--mg-text);
  font-size: 15px;
  font-weight: 950;
}

.mgq-step-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--mg-border);
  background: rgba(248, 251, 255, 0.72);
}

.mgq-step {
  min-height: 82px;
  padding: 13px;
  border-radius: 15px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.92);
  cursor: pointer;
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 10px;
  text-align: left;
  align-items: start;
  transition: 0.16s ease;
}

.mgq-step > span {
  width: 30px;
  height: 30px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  font-size: 12px;
  font-weight: 950;
}

.mgq-step b {
  display: block;
  color: var(--mg-text);
  font-size: 12px;
  font-weight: 950;
  line-height: 1.25;
}

.mgq-step small {
  display: block;
  margin-top: 4px;
  color: var(--mg-muted);
  font-size: 10px;
  font-weight: 750;
  line-height: 1.25;
}

.mgq-step.active {
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  border-color: transparent;
  box-shadow: 0 14px 28px rgba(31, 123, 193, 0.18);
}

.mgq-step.active > span {
  background: rgba(255,255,255,0.20);
  color: white;
}

.mgq-step.active b,
.mgq-step.active small {
  color: white;
}

.mgq-step.done {
  border-color: rgba(31, 123, 193, 0.28);
}

.mgq-form-body {
  padding: 24px 26px;
}

.mgq-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.mgq-field {
  display: grid;
  gap: 8px;
}

.mgq-field.full-span,
.full-span {
  grid-column: 1 / -1;
}

.mgq-field label {
  color: #30445d;
  font-size: 12px;
  font-weight: 950;
}

.mgq-field small {
  color: var(--mg-muted);
  font-size: 11px;
  font-weight: 750;
  line-height: 1.45;
}

.mgq-field input,
.mgq-field select,
.mgq-field textarea {
  width: 100%;
  min-height: 48px;
  padding: 12px 13px;
  border-radius: 13px;
  border: 1px solid var(--mg-border);
  background: #f7fbff;
  color: var(--mg-text);
  font-size: 14px;
  font-weight: 700;
  outline: none;
  transition: 0.16s ease;
}

.mgq-field textarea {
  min-height: 122px;
  resize: vertical;
  line-height: 1.55;
}

.mgq-field input::placeholder,
.mgq-field textarea::placeholder {
  color: #8aa0b8;
  font-weight: 600;
}

.mgq-field input:focus,
.mgq-field select:focus,
.mgq-field textarea:focus {
  background: #ffffff;
  border-color: #1f7bc1;
  box-shadow: 0 0 0 4px rgba(31, 123, 193, 0.12);
}

.mgq-footer {
  position: sticky;
  bottom: 0;
  z-index: 10;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 26px;
  border-top: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.88);
  backdrop-filter: blur(16px);
}

.mgq-btn {
  min-height: 42px;
  padding: 10px 18px;
  border-radius: 12px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.16s ease;
  white-space: nowrap;
}

.mgq-btn:disabled {
  opacity: 0.52;
  cursor: not-allowed;
}

.mgq-btn.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 14px 28px rgba(31, 123, 193, 0.22);
}

.mgq-btn.ghost {
  color: #112238;
  background: #f8fbff;
  border-color: var(--mg-border);
}

@media (max-width: 1080px) {
  .mgq-hero {
    grid-template-columns: 1fr;
  }

  .mgq-signal-card {
    max-width: 620px;
  }

  .mgq-step-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 680px) {
  .mgq-hero-copy,
  .mgq-signal-card {
    padding: 20px;
  }

  .mgq-hero h2 {
    font-size: 34px;
  }

  .mgq-mode-switch {
    grid-template-columns: 1fr;
  }

  .mgq-progress-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .mgq-step-strip {
    grid-template-columns: 1fr;
  }

  .mgq-grid {
    grid-template-columns: 1fr;
  }

  .mgq-form-body {
    padding: 20px;
  }

  .mgq-footer {
    flex-direction: column;
    padding: 14px 20px;
  }

  .mgq-btn {
    width: 100%;
  }
}
`;